<template>
  <div class="font-family-block">
    <div class="font-family-row" :class="{ 'hidden': !displayProperties }">
      <Dropdown
        v-model="selectedFontFamily"
        data-test-id="font-family-dropdown"
        class="font-family-dropdown flex-grow-1"
        :placeholder="t(configs.label)"
        :options="fontFamilyList"
        option-label="label"
        option-value="value"
        dropdown-class="stepper-input"
      >
        <template #option="slotProps">
          <div class="p-dropdown-name">
            <span>{{ slotProps.option.label }}</span>
          </div>
        </template>
      </Dropdown>

      <!-- Google Fonts button -->
      <Button
        v-if="showGoogleFontsButton"
        v-tooltip.top="t('googleFonts.browseButton')"
        icon="fas fa-plus"
        class="p-button-outlined p-button-sm google-fonts-btn"
        @click="openGoogleFontsModal"
      />
    </div>

    <!-- Fallback font selector (email templates with non-compatible font) -->
    <div v-if="showFallbackSelector" class="fallback-font-row mt-2" :class="{ 'hidden': !displayProperties }">
      <label class="fallback-label">{{ t('googleFonts.fallbackFont') }}</label>
      <Dropdown
        v-model="selectedFallbackFont"
        class="fallback-dropdown"
        :options="fallbackFontOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('googleFonts.selectFallback')"
      />
      <Button
        v-tooltip.top="isPreviewingFallback ? t('googleFonts.hideFallbackPreview') : t('googleFonts.showFallbackPreview')"
        :class="['p-button-sm p-button-text fallback-preview-btn', { 'active': isPreviewingFallback }]"
        @click="toggleFallbackPreview"
      >
        <i v-if="!isPreviewingFallback" class="fas fa-eye" />
        <span v-else class="countdown-badge">{{ previewCountdown }}</span>
      </Button>
    </div>

    <!-- Google Fonts Modal -->
    <GoogleFontPickerModal
      v-if="showGoogleFontsButton"
      v-model:visible="isGoogleFontsModalVisible"
      :current-font="currentGoogleFont"
      :is-email-template="isEmailTemplateType"
      :iframe-document="iframeDocument"
      @select="handleGoogleFontSelect"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, ref, Ref, watch, computed, onMounted,
} from 'vue';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';

import { FieldConfig, ParserFieldObject, Property } from '@/types';
import getFontFamilyList from '@/configs/font-family';
import GoogleFontPickerModal from '@/components/modals/GoogleFontPickerModal.vue';
import { isDisplayTemplate, isEmailTemplate, getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { useGoogleFonts } from '@/composables/google-fonts/useGoogleFonts';

export default defineComponent({
  name: 'FontFamily',
  components: {
    Dropdown,
    Button,
    GoogleFontPickerModal,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const configs = computed(() => props.configs);
    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(configs.value, 'displayToggle') || configs.value.displayToggle === true));
    const property: Ref<Property> = ref(props.parserValues.properties && props.parserValues.properties[0]);
    const selectedFontFamily = ref(property.value.value ?? props.configs.options.defaultValue);
    const fontFamilyList = ref<any[]>(getFontFamilyList(t));

    // Google Fonts integration
    const isGoogleFontsModalVisible = ref(false);
    const { injectFontToIframe, generateFontValue, getDetectedTemplateFonts, isEmailSafe } = useGoogleFonts();

    // Check if Google Fonts button should be shown (display and email templates)
    const showGoogleFontsButton = computed(() => {
      const templateType = TemplateEditorState?.template?.type;
      return isDisplayTemplate(templateType) || isEmailTemplate(templateType);
    });

    // Check if current template is email type (for warning in modal)
    const isEmailTemplateType = computed(() => {
      const templateType = TemplateEditorState?.template?.type;
      return isEmailTemplate(templateType);
    });

    // Get iframe document for font injection
    const iframeDocument = computed(() => getTemplateIframeDocument());

    // Fallback font options (web-safe fonts from main dropdown)
    const fallbackFontOptions = [
      { label: 'Georgia', value: 'Georgia' },
      { label: 'Palatino Linotype', value: 'Palatino Linotype' },
      { label: 'Times New Roman', value: 'Times New Roman' },
      { label: 'Arial', value: 'Arial' },
      { label: 'Arial Black', value: 'Arial Black' },
      { label: 'Comic Sans MS', value: 'Comic Sans MS' },
      { label: 'Impact', value: 'Impact' },
      { label: 'Lucida Sans Unicode', value: 'Lucida Sans Unicode' },
      { label: 'Tahoma', value: 'Tahoma' },
      { label: 'Trebuchet MS', value: 'Trebuchet MS' },
      { label: 'Verdana', value: 'Verdana' },
      { label: 'Courier New', value: 'Courier New' },
      { label: 'Lucida Console', value: 'Lucida Console' },
    ];
    const selectedFallbackFont = ref('Arial');
    const isPreviewingFallback = ref(false);
    const previewCountdown = ref(3);
    let fallbackPreviewTimeout: ReturnType<typeof setTimeout> | null = null;
    let countdownInterval: ReturnType<typeof setInterval> | null = null;

    // Remove fallback preview CSS and clear timers
    const removeFallbackPreview = () => {
      // Clear timers
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      if (fallbackPreviewTimeout) {
        clearTimeout(fallbackPreviewTimeout);
        fallbackPreviewTimeout = null;
      }

      const iframeDoc = getTemplateIframeDocument();
      if (iframeDoc) {
        const styleId = 'spm-fallback-preview-style';
        const styleElement = iframeDoc.getElementById(styleId);
        if (styleElement) {
          styleElement.remove();
        }
      }

      isPreviewingFallback.value = false;
      previewCountdown.value = 3;
    };

    // Toggle fallback preview in iframe - inject CSS rule to override all styles
    const toggleFallbackPreview = () => {
      // Clear any existing timeout
      if (fallbackPreviewTimeout) {
        clearTimeout(fallbackPreviewTimeout);
        fallbackPreviewTimeout = null;
      }

      // If already previewing, just stop
      if (isPreviewingFallback.value) {
        removeFallbackPreview();
        return;
      }

      isPreviewingFallback.value = true;

      const iframeDoc = getTemplateIframeDocument();
      if (!iframeDoc) return;

      const selector = props.parserValues.selector;
      if (!selector) return;

      const styleId = 'spm-fallback-preview-style';
      let styleElement = iframeDoc.getElementById(styleId) as HTMLStyleElement;

      // Create or update style element with fallback font
      if (!styleElement) {
        styleElement = iframeDoc.createElement('style');
        styleElement.id = styleId;
        iframeDoc.head.appendChild(styleElement);
      }
      // Use the selector to target elements and force fallback font
      styleElement.textContent = `${selector} { font-family: "${selectedFallbackFont.value}", sans-serif !important; }`;

      // Start countdown
      previewCountdown.value = 3;
      countdownInterval = setInterval(() => {
        previewCountdown.value -= 1;
        if (previewCountdown.value <= 0) {
          removeFallbackPreview();
        }
      }, 1000);
    };

    // Check if selected font is a Google Font (not email-safe)
    const isGoogleFontSelected = computed(() => {
      const fontName = currentGoogleFont.value;
      if (!fontName) return false;
      return !isEmailSafe(fontName);
    });

    // Show fallback selector only for email templates with non-compatible font
    const showFallbackSelector = computed(() => {
      return isEmailTemplateType.value && isGoogleFontSelected.value;
    });

    // Extract current Google Font name from value
    const currentGoogleFont = computed(() => {
      const value = selectedFontFamily.value;
      if (!value) return '';
      // Extract font name from value like '"Roboto", sans-serif'
      const match = value.match(/"([^"]+)"/);
      return match ? match[1] : '';
    });

    // Open Google Fonts modal
    const openGoogleFontsModal = () => {
      isGoogleFontsModalVisible.value = true;
    };

    // Build font-family value with fallback for email templates
    const buildFontValueWithFallback = (fontFamily: string, category: string, fallback: string): string => {
      return `"${fontFamily}", "${fallback}", ${category}`;
    };

    // Handle Google Font selection
    const handleGoogleFontSelect = (fontData: { family: string; value: string; isEmailSafe: boolean }) => {
      let finalValue = fontData.value;

      // For email templates with non-compatible font, include fallback
      if (isEmailTemplateType.value && !fontData.isEmailSafe) {
        // Extract category from original value (e.g., "Roboto", sans-serif -> sans-serif)
        const categoryMatch = fontData.value.match(/,\s*([a-z-]+)$/);
        const category = categoryMatch ? categoryMatch[1] : 'sans-serif';
        finalValue = buildFontValueWithFallback(fontData.family, category, selectedFallbackFont.value);
      }

      // Add to dropdown options if not already present
      const existingOption = fontFamilyList.value.find((f) => f.value === finalValue);
      if (!existingOption) {
        fontFamilyList.value.push({
          label: fontData.family,
          value: finalValue,
          isGoogleFont: true,
        });
      }

      // Select the font
      selectedFontFamily.value = finalValue;

      // Inject font CSS into iframe
      const iframeDoc = getTemplateIframeDocument();
      if (iframeDoc && !fontData.isEmailSafe) {
        injectFontToIframe(fontData.family, iframeDoc);
      }
    };

    // Watch fallback font changes and update selected font value
    watch(selectedFallbackFont, (newFallback) => {
      if (isEmailTemplateType.value && isGoogleFontSelected.value) {
        const fontName = currentGoogleFont.value;
        if (fontName) {
          // Extract category from current value
          const categoryMatch = selectedFontFamily.value.match(/,\s*([a-z-]+)$/);
          const category = categoryMatch ? categoryMatch[1] : 'sans-serif';
          const newValue = buildFontValueWithFallback(fontName, category, newFallback);

          // Update dropdown option
          const optionIndex = fontFamilyList.value.findIndex((f) => f.label === fontName);
          if (optionIndex !== -1) {
            fontFamilyList.value[optionIndex].value = newValue;
          }

          // Update selected value
          selectedFontFamily.value = newValue;
        }
      }
    });

    // On mount, add detected Google Fonts to dropdown (already scanned by TemplateLiveView)
    onMounted(() => {
      if (showGoogleFontsButton.value) {
        // Get fonts already detected during template load
        const detectedFonts = getDetectedTemplateFonts();

        // Add detected fonts to dropdown if not already present
        detectedFonts.forEach((option) => {
          const exists = fontFamilyList.value.find((f) => f.value === option.value);
          if (!exists) {
            fontFamilyList.value.push(option);
          }
        });

        // If current value is a Google Font not in dropdown, add it with exact value from template
        if (selectedFontFamily.value) {
          const currentFontName = currentGoogleFont.value;
          const exactValueExists = fontFamilyList.value.find((f) => f.value === selectedFontFamily.value);

          if (currentFontName && !exactValueExists) {
            // Add option with exact template value (preserves original category)
            fontFamilyList.value.push({
              label: currentFontName,
              value: selectedFontFamily.value,
              isGoogleFont: true,
            });
          }
        }
      }
    });

    watch(selectedFontFamily, () => {
      if (property.value) {
        property.value.value = selectedFontFamily.value;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    });

    return {
      t,
      displayProperties,
      selectedFontFamily,
      fontFamilyList,
      // Google Fonts
      showGoogleFontsButton,
      isEmailTemplateType,
      iframeDocument,
      currentGoogleFont,
      isGoogleFontsModalVisible,
      openGoogleFontsModal,
      handleGoogleFontSelect,
      // Fallback font
      showFallbackSelector,
      selectedFallbackFont,
      isPreviewingFallback,
      toggleFallbackPreview,
      fallbackFontOptions,
      previewCountdown,
    };
  },
});
</script>

<style scoped lang="scss">
.p-dropdown-name {
  white-space: break-spaces;
  width: $configuration-panel-width - 5.25rem;
}

.font-family-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.font-family-block .font-family-dropdown {
  border: 1px solid $heather;
  border-radius: $field-border-radius;
  flex: 1;
  min-width: 0;

  &:hover {
    border-color: $brand-color-primary;
    color: $brand-color-primary;
    box-shadow: none;
  }

  &.p-focus {
    border-color: $brand-color-primary;
    color: $brand-color-primary;
    box-shadow: $box-shadow;
  }

  &:enabled:focus {
    box-shadow: none;
  }

  :deep() .p-dropdown-label.p-inputtext {
    font-size: .9375rem;
    line-height: 1.25rem;
    padding: .1875rem;
  }
}

.google-fonts-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;

  &:hover {
    background-color: $brand-color-primary;
    color: white;
  }
}

.fallback-font-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;

  .fallback-label {
    font-size: 0.8rem;
    color: #666;
    white-space: nowrap;
  }

  .fallback-dropdown {
    flex: 1;
    border: 1px solid $heather;
    border-radius: $field-border-radius;

    :deep() .p-dropdown-label.p-inputtext {
      font-size: 0.85rem;
      padding: 0.15rem 0.3rem;
    }
  }

  .fallback-preview-btn {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    padding: 0;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: $brand-color-primary;
      background-color: rgba($brand-color-primary, 0.1);
    }

    &.active {
      color: white;
      background-color: $brand-color-primary;

      &:hover {
        background-color: darken($brand-color-primary, 10%);
      }
    }

    .countdown-badge {
      font-size: 0.9rem;
      font-weight: bold;
      min-width: 16px;
    }
  }
}
</style>
